<template lang="pug">
div(v-if="!audio")
  v-btn.goal-btn.text-14( block depressed @click="toggleRecording" ) {{ !isRecording ? 'Записать' : 'Остановить' }}
    v-icon(v-if="!isRecording" size=24) mdi-microphone
    v-icon(v-else size=24) mdi-record-circle-outline

div(v-else).voice
  .voice-container
    v-btn.play( icon small depressed @click="togglePlay")
      v-icon( size=18 color="white" ) {{ playing ? 'mdi-pause' : 'mdi-play' }}
    
    .wave-container
      canvas.wave( ref="wave" )

  .voice-actions
    v-btn.icon-btn( icon depressed @click="deleteSpeech" )
      v-icon( size=24 color="black" ) mdi-delete
    v-btn.icon-btn( icon depressed @click="toggleRecording" )
      v-icon( size=24 color="black" ) mdi-microphone

  audio( ref="audio" :src="audio" @ended="playing=false" )
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {  
data() {
  return {
    playing: false,
    isRecording: false,
    mediaRecorder: null,
    audioChunks: [],
    audioLine: null,
    audioLineURL: null
  }
},

props: {
  audioUrl: String
},

mounted() {
  if(this.audio) this.drawWave()
  document.addEventListener("visibilitychange", this.handleVisibilityChange)
},

beforeDestroy() {
  document.removeEventListener("visibilitychange", this.handleVisibilityChange)
},

watch: {
  audio() {
    this.$nextTick(() => {
      this.drawWave()
    })
  }
},

computed: {
  ...mapState('goals', ['selectedGoal']),

  audio() {
    return this.audioUrl || this.audioLineURL
  }
},

methods: {
  ...mapActions('goals', ['sendNewSpeech']),

  deleteSpeech() {
    this.$emit('deleteAudio')
    this.audioLineURL = null
  },

  async toggleRecording() {
    this.deleteSpeech()
    if (!this.isRecording) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaRecorder = new MediaRecorder(stream, {audioBitsPerSecond: 128000})
      this.audioChunks = []

      this.mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) this.audioChunks.push(e.data)
      }
      
      this.mediaRecorder.onstop = async () => {
        const rawBlob = new Blob(this.audioChunks, { type: this.audioChunks[0]?.type })
        this.audioLine = await this.convertToWav(rawBlob)
        this.audioLineURL = URL.createObjectURL(this.audioLine)
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
        this.uploadSpeech()
      }

      this.mediaRecorder.start()
      this.isRecording = true
    } else {
      this.mediaRecorder.stop()
      this.isRecording = false
    }
  },

  async convertToWav(blob) {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const arrayBuffer = await blob.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    
    const worker = {
      recLength: audioBuffer.length,
      recBuffers: [audioBuffer.getChannelData(0)],
      sampleRate: audioBuffer.sampleRate
    }

    const buffer = new ArrayBuffer(44 + worker.recLength * 2)
    const view = new DataView(buffer)

    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i))
    }

    writeString(0, 'RIFF')
    view.setUint32(4, 32 + worker.recLength * 2, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, 1, true)
    view.setUint32(24, worker.sampleRate, true)
    view.setUint32(28, worker.sampleRate * 2, true)
    view.setUint16(32, 2, true)
    view.setUint16(34, 16, true)
    writeString(36, 'data')
    view.setUint32(40, worker.recLength * 2, true)

    let offset = 44
    for (let i = 0; i < worker.recBuffers[0].length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, worker.recBuffers[0][i]))
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
    }

    return new Blob([view], { type: 'audio/wav' })
  },

  uploadSpeech() {
    if(!this.audioLine) return
    const file = new File([this.audioLine], `speech_${this.selectedGoal.uuid}.wav`, {type: 'wav'})

    let formData = new FormData()
    formData.append("state", 1)
    formData.append("file", file)
    formData.append("_file", file.name)

    let obj = { goalsUuid: this.selectedGoal.uuid, audio: formData }
    this.sendNewSpeech(obj).then(() => {
      this.$emit('loadSpeech')
    })
  },

  handleVisibilityChange() {
    if (document.hidden && this.isRecording) {
      if (this.mediaRecorder?.state === "recording") this.mediaRecorder.stop()
      this.isRecording = false
    }
  },

  togglePlay() {
    const audio=this.$refs.audio

    this.playing ? audio.pause() : audio.play()
    this.playing = !this.playing
  },

  drawWave() {
    const canvas = this.$refs.wave
    if (!canvas) return

    const ctx = canvas.getContext("2d")

    const containerWidth = canvas.clientWidth || 220 
    const containerHeight = 24

    canvas.width = containerWidth
    canvas.height = containerHeight
    
    ctx.clearRect(0, 0, containerWidth, containerHeight)

    const barWidth = 3
    const gap = 2
    const barsCount = Math.floor(containerWidth / (barWidth + gap))

    ctx.fillStyle = "#4c6fff"

    for (let i = 0; i < barsCount; i++) {
      const h = Math.random() * 18 + 3
      const y = (containerHeight - h) / 2
      ctx.fillRect(i * (barWidth + gap), y, barWidth, h)
    }
  }
}
}
</script>

<style scoped>
.goal-btn {
  background: #E5E7EB;
  border-radius: 6px;
  text-transform: none;
  height: 36px;
}
.goal-btn.active {
  background-color: #b8d1ff !important;
  color: black;
}
.voice{
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.wave-container {
  flex: 1;
  height: 24px;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.wave {
  width: 100%;
  height: 100%;
}
.actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.voice-container {
  display: flex;
  align-items: center;
  background: #e9eaee;
  border-radius: 20px;
  padding: 6px 10px;
  gap: 10px;
  flex: 1;
  height: 36px;
  min-width: 0;
}
.play {
  background-color: #4c6fff;
  flex-shrink: 0;
}
.voice-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.icon{
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>